/**
 * Boss HP banner shown above the canvas during boss levels.
 *
 * Reads remaining boss HP by summing live brick HP — we don't track
 * this in state because `score` re-renders cover every brick break, so
 * the banner stays in sync without extra plumbing. Renders nothing
 * when there's no active boss (`levelCfg.boss` falsy) or the blocks
 * array isn't ready yet.
 *
 * Extracted from `GameCanvas.jsx` on 2026-05-13. Self-contained — only
 * needs the live `blocks` array, the level config, and an optional
 * `data-testid` override (so embed-tests can scope to one banner).
 */
export function BossBanner({ levelCfg, blocks }) {
  if (!levelCfg?.boss || !blocks) return null;
  const remaining = blocks.reduce((a, b) => a + (b.hp || 0), 0);
  const total = levelCfg.boss.totalHp || 1;
  const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
  return (
    <div className="w-full max-w-[800px]" data-testid="boss-banner">
      <div
        className="border border-[#ff007f] bg-black/60 px-4 py-2 flex items-center gap-3"
        style={{ boxShadow: "0 0 20px rgba(255,0,127,0.45)" }}
      >
        <span className="font-mono-cosmic text-[9px] tracking-[0.35em] text-[#ff007f] uppercase">
          // BOSS
        </span>
        <span
          className="font-heading uppercase tracking-tight text-base sm:text-lg text-white truncate"
          data-testid="boss-name"
        >
          {levelCfg.boss.name}
        </span>
        <div className="flex-1 h-3 border border-[#ff007f]/60 bg-black/80 relative overflow-hidden ml-2">
          <div
            className="absolute inset-y-0 left-0 transition-[width] duration-300 ease-out"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg,#ff007f 0%,#ffd700 60%,#00ff41 100%)",
              boxShadow: "0 0 10px rgba(255,0,127,0.7)",
            }}
            data-testid="boss-hp-fill"
          />
        </div>
        <span
          className="font-mono-cosmic text-[10px] tracking-[0.25em] text-[#ff007f] uppercase tabular-nums"
          data-testid="boss-hp-text"
        >
          {Math.ceil(pct)}%
        </span>
      </div>
    </div>
  );
}
